import { axiosInstance, type CustomResponseFormat } from "api/instance";

export const CHAT_DOMAIN = "chat";

export const CHAT_DETAIL_ENDPOINT = "detail";
export const CHAT_MESSAGE_LIST_ENDPOINT = "message/list";
export const CHAT_SEND_MESSAGE_ENDPOINT = "message/send";
export const CHAT_READ_MESSAGE_ENDPOINT = "message/read";

export const CHAT_UPDATE_DETAIL_ENDPOINT = "message/task-update/detail";
export const CHAT_AI_MESSAGE_DETAIL_ENDPOINT = "message/ai/detail";
export const CHAT_UPDATE_SEND_ENDPOINT = "message/task-update/send";
export const CHAT_REMIND_BULK_SEND_ENDPOINT = "message/remind-bulk/send";
export const CHAT_REMIND_HISTORY_DETAIL_ENDPOINT = "remind/history/detail";

export interface ChatType {
  isMine: boolean;
  nickname: string;
  message: string;
  type: string;
  time: string;
  messageId: number;
}

export interface AIMessageType {
  aiMessageId: number;
  message: string;
}

export interface UpdateMessageType {
  taskUpdateMessageId: string;
  message: string;
  status: string;
  evalReason?: string;
}

export interface RemindHistoryType {
  shortTermSentAt?: string;
  midTermSentAt?: string;
  longTermSentAt?: string;
}

export interface ChannelConnectionType {
  chatConnection: ChannelInfoType;
  isSendAvailable: boolean;
  unreadMessageCount?: number;
  subscription: SubscriptionState;
}

export interface ChannelInfoType {
  channelName: string;
  channelUrl: string;
  userAccessToken: string;
  userId: string;
  userNickname: string;
}

export interface SubscriptionState {
  hasStarted: boolean;
  hasFreeTrialUsed?: boolean;
  hasFirstMonthBenefitReceived: boolean;
  isSubscribing: boolean;
  expiryDaysLeft?: number;
}

export const chatApi = {
  getDetail: () =>
    axiosInstance.get<CustomResponseFormat<ChannelConnectionType | undefined>>(
      `${CHAT_DOMAIN}/${CHAT_DETAIL_ENDPOINT}`
    ),

  getList: (pid: string, mid_end_exclusive?: number) =>
    axiosInstance.get<CustomResponseFormat<ChatType[]>>(
      `${CHAT_DOMAIN}/${CHAT_MESSAGE_LIST_ENDPOINT}`,
      { params: { pid: pid, mid_end_exclusive: mid_end_exclusive } }
    ),

  send: (
    pid: string,
    message: string,
    isTaskUpdateMessage: boolean,
    aiMessageId?: number
  ) =>
    axiosInstance.post<void>(
      `${CHAT_DOMAIN}/${CHAT_SEND_MESSAGE_ENDPOINT}`,
      {
        message: message,
        isTaskUpdateMessage: isTaskUpdateMessage,
        aiMessageId: aiMessageId,
      },
      { params: { pid: pid } }
    ),
  read: (pid?: string) =>
    axiosInstance.get<CustomResponseFormat>(
      `${CHAT_DOMAIN}/${CHAT_READ_MESSAGE_ENDPOINT}`,
      { params: { pid: pid } }
    ),

  getUpdateMessageDetail: (pid: string, tu_mid?: string) =>
    axiosInstance.get<CustomResponseFormat<UpdateMessageType>>(
      `${CHAT_DOMAIN}/${CHAT_UPDATE_DETAIL_ENDPOINT}`,
      { params: { pid: pid, ...(tu_mid && { tu_mid: tu_mid }) } }
    ),

  getAiMessageDetail: (pid: string) =>
    axiosInstance.get<CustomResponseFormat<AIMessageType | undefined>>(
      `${CHAT_DOMAIN}/${CHAT_AI_MESSAGE_DETAIL_ENDPOINT}`,
      { params: { pid: pid } }
    ),

  getRemindHistoryDetail: () =>
    axiosInstance.get<CustomResponseFormat<RemindHistoryType>>(
      `${CHAT_DOMAIN}/${CHAT_REMIND_HISTORY_DETAIL_ENDPOINT}`
    ),

  sendUpdateMessage: (
    pid: string,
    messageId: string,
    message: string,
    reason?: string
  ) =>
    axiosInstance.post<void>(
      `${CHAT_DOMAIN}/${CHAT_UPDATE_SEND_ENDPOINT}`,
      {
        messageId: messageId,
        message: message,
        reason: reason,
      },
      { params: { pid: pid } }
    ),

  sendBulkRemind: (
    shortTermTargetPatientIdList?: number[],
    midTermTargetPatientIdList?: number[],
    longTermTargetPatientIdList?: number[],
    shortTermMessage?: string,
    midTermMessage?: string,
    longTermMessage?: string
  ) =>
    axiosInstance.post<void>(
      `${CHAT_DOMAIN}/${CHAT_REMIND_BULK_SEND_ENDPOINT}`,
      {
        shortTermTargetPatientIdList: shortTermTargetPatientIdList,
        midTermTargetPatientIdList: midTermTargetPatientIdList,
        longTermTargetPatientIdList: longTermTargetPatientIdList,
        shortTermMessage: shortTermMessage,
        midTermMessage: midTermMessage,
        longTermMessage: longTermMessage,
      }
    ),
};